import * as React from "react";
import { KeyRound, Loader2, Save } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  PageHeader,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  toast,
} from "@eduviaos/ui-kit";
import { usePlatformRazorpay, useReplacePlatformRazorpay } from "@/lib/platformRazorpay/api";

export function PlatformRazorpayPage() {
  const configQuery = usePlatformRazorpay();
  const saveMutation = useReplacePlatformRazorpay();
  const [keyId, setKeyId] = React.useState("");
  const [keySecret, setKeySecret] = React.useState("");
  const [webhookSecret, setWebhookSecret] = React.useState("");
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    if (!configQuery.data) return;
    setKeyId(configQuery.data.key_id);
    setKeySecret("");
    setWebhookSecret("");
    setDirty(false);
  }, [configQuery.data]);

  const secretSet = configQuery.data?.key_secret_set === true;
  const keyChanged = keyId.trim() !== (configQuery.data?.key_id ?? "");
  const canSave =
    dirty &&
    !saveMutation.isPending &&
    keyId.trim().length > 0 &&
    (secretSet && !keyChanged ? true : keySecret.trim().length > 0);

  async function handleSave() {
    try {
      await saveMutation.mutateAsync({
        key_id: keyId.trim(),
        key_secret: keySecret.trim(),
        webhook_secret: webhookSecret.trim(),
      });
      toast.success("EduViaOS Razorpay key saved.");
      setKeySecret("");
      setWebhookSecret("");
      setDirty(false);
    } catch {
      toast.error("Couldn't save the Razorpay key. Check the Key ID and secret.");
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 pb-12 lg:p-8">
      <PageHeader>
        <PageHeaderHeading>
          <PageHeaderTitle>Billing</PageHeaderTitle>
          <PageHeaderDescription>
            The Razorpay account EduViaOS uses when a centre chooses EduViaOS to collect learner app
            payments. Replace it here before production. Saved secrets stay on the server.
          </PageHeaderDescription>
        </PageHeaderHeading>
      </PageHeader>

      {configQuery.isLoading ? (
        <Card>
          <CardContent className="text-muted-foreground flex items-center gap-2 py-8 text-sm">
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            Loading Razorpay settings…
          </CardContent>
        </Card>
      ) : configQuery.isError ? (
        <Card>
          <CardContent className="text-destructive py-8 text-sm">
            Couldn&apos;t load the EduViaOS Razorpay account.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <KeyRound className="text-muted-foreground size-5" aria-hidden="true" />
              <CardTitle>EduViaOS Razorpay</CardTitle>
            </div>
            <CardDescription>
              {configQuery.data?.source === "stored"
                ? "This key is stored for the platform and overrides the server environment."
                : configQuery.data?.source === "environment"
                  ? "The server environment still supplies the key. Saving here replaces it."
                  : "No platform key is set yet."}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid max-w-md gap-4">
            <div className="grid gap-2">
              <Label htmlFor="platform-razorpay-key">Key ID</Label>
              <Input
                id="platform-razorpay-key"
                autoComplete="off"
                placeholder="rzp_test_…"
                value={keyId}
                onChange={(event) => {
                  setKeyId(event.target.value);
                  setDirty(true);
                }}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="platform-razorpay-secret">Key Secret</Label>
              <Input
                id="platform-razorpay-secret"
                type="password"
                autoComplete="new-password"
                placeholder={secretSet ? "Saved. Enter a new secret to replace it." : ""}
                value={keySecret}
                onChange={(event) => {
                  setKeySecret(event.target.value);
                  setDirty(true);
                }}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="platform-razorpay-webhook">Webhook secret</Label>
              <Input
                id="platform-razorpay-webhook"
                type="password"
                autoComplete="new-password"
                placeholder={
                  configQuery.data?.webhook_secret_set
                    ? "Saved. Enter a new secret to replace it."
                    : "Optional"
                }
                value={webhookSecret}
                onChange={(event) => {
                  setWebhookSecret(event.target.value);
                  setDirty(true);
                }}
              />
              <p className="text-muted-foreground text-xs">
                Checkout can open without this. Add it so Razorpay can confirm a payment if the
                learner closes the window.
              </p>
            </div>
            <Button type="button" disabled={!canSave} onClick={() => void handleSave()}>
              {saveMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              ) : (
                <Save className="size-4" aria-hidden="true" />
              )}
              Save key
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
